import { FormWithRedirect } from 'ra-core'
import { SimpleFormView } from 'ra-ui-materialui/src/form/SimpleForm'
import { useRef } from 'react'
import { UploadFileProvider, useFileUploadContext } from '../controller/UploadFileContext'

type UploadFilesFormProps = {
  orgId: number
  projectUrl?: string
  projectId?: string
  save?: (values: any, redirect?: any, callbacks?: any) => void
  children?: React.ReactNode
  [key: string]: any // Accept all other props that SimpleForm/FormWithRedirect accepts
}

/**
 * Inner form component that handles upload before submission
 */
const UploadFilesFormInner = ({
  save,
  ...formProps
}: Omit<UploadFilesFormProps, 'orgId' | 'projectUrl' | 'projectId'>) => {
  const { uploadAll, totalPendingFilesCount } = useFileUploadContext()
  const formRef = useRef<any>(null)

  // Wrap the save function to intercept and upload files first
  const handleSave = async (values: any, redirect?: any, callbacks?: any) => {
    // If there are files to upload, upload them first
    if (totalPendingFilesCount > 0) {
      try {
        // Upload all files - wait for completion
        await uploadAll()

        // UploadFileInput components will update their form fields via handlers registered with updateFiles
        // We need to wait for those updates, then get the updated form values
        await new Promise((resolve) => setTimeout(resolve, 50)) // Small delay to allow form field updates

        // Get updated values from the stored form reference
        // UploadFileInput handlers have already been called by the context
        let updatedValues = values
        if (formRef.current) {
          updatedValues = formRef.current.getState().values
        }

        // Wrap the save callbacks to reset upload status after successful save
        const wrappedCallbacks = {
          ...callbacks,
          onSuccess: (response: any) => {
            // Reset upload status after successful save so we can sync response data
            // This allows useSyncUploadFileToField to process the response data properly
            setTimeout(() => {
              // Call original onSuccess if provided
              if (callbacks?.onSuccess) {
                callbacks.onSuccess(response)
              }
            }, 100) // Small delay to allow form to update with response data
          },
        }

        // Now save with updated values
        if (save) {
          return save(updatedValues, redirect, wrappedCallbacks)
        }
      } catch (error) {
        // Error handling - call onFailure if provided
        if (callbacks?.onFailure) {
          callbacks.onFailure(error as Error)
        }
        return
      }
    } else {
      // No files to upload, save normally
      if (save) {
        return save(values, redirect, callbacks)
      }
    }
  }

  return (
    <FormWithRedirect
      save={handleSave}
      {...(formProps as any)}
      render={(renderProps) => {
        // Store form reference for accessing updated values after upload
        const form = (renderProps as any).form
        if (form) {
          formRef.current = form
        }
        return <SimpleFormView {...(renderProps as any)} />
      }}
    />
  )
}

/**
 * Form wrapper that integrates UploadFileContext with react-admin's FormWithRedirect.
 * Handles file uploads before form submission by intercepting the save function.
 * This component is a drop-in replacement for SimpleForm when file uploads are needed.
 */
export const UploadFilesForm = ({ orgId, projectUrl, projectId, save, ...formProps }: UploadFilesFormProps) => {
  return (
    <UploadFileProvider orgId={orgId} projectUrl={projectUrl} projectId={projectId}>
      <UploadFilesFormInner save={save} {...formProps} />
    </UploadFileProvider>
  )
}
